import { BadgeLink } from "@/components/links";
import { NationalIdCard } from "@/components/national-id-card";
import { StatePage } from "@/components/state-page";
import { filterKeys, formatCurrency, getImageUrl, stringToRandomNumber } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import { ActionIcon, Anchor, Button, Card, Skeleton, useModalsStack } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";
import type { AdminClient } from "@workspace/admin-domain/src/features/client/schema";
import type { AdminTrip } from "@workspace/admin-domain/src/features/trip/schema";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import {
  LucideArrowLeft,
  LucideCalendar,
  LucideChartArea,
  LucideEdit,
  LucideMail,
  LucideMoreVertical,
  LucideNotepadText,
  LucidePhone,
  LucideSwatchBook,
  LucideUser,
} from "lucide-react";
import * as z from "zod";
import { ReservationFiltersSchema } from "../../-reservations/-utils";
import { DEFAULT_TRIPS_PARAMS, TripFiltersSchema } from "../../trips/-utils";
import EditClient from "./-edit";
import ClientMenu from "./-menu";
import { ClientReservations, ClientReservationsSkeleton } from "./-reservations";
import { ClientTrips, ClientTripsSkeleton } from "./-trips";

export const Route = createFileRoute("/_authenticated/clients/$id/")({
  validateSearch: z.object({
    ...ReservationFiltersSchema.shape,
    ...TripFiltersSchema.shape,
  }),
  component: Component,
  beforeLoad: requirePermissions("client:get"),
  loaderDeps: ({ search }) => filterKeys(search, TripFiltersSchema.shape),
  loader: ({ context, params, deps }) => {
    void context.queryClient.prefetchQuery(
      orpc.client.get.queryOptions({
        input: { id: params.id },
      }),
    );
    void context.queryClient.prefetchQuery(
      orpc.reservation.getAll.queryOptions({
        input: { value: params.id, type: "agencyClientId" },
      }),
    );
    void context.queryClient.prefetchInfiniteQuery(
      orpc.trip.getAll.infiniteOptions({
        input: (paginate) => ({
          ...DEFAULT_TRIPS_PARAMS,
          ...deps,
          clientId: params.id,
          paginate,
        }),
        initialPageParam: undefined as AdminTrip["GetAll"]["paginate"],
        getNextPageParam: () => undefined as AdminTrip["GetAll"]["paginate"],
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="client" emptyResourceType="client" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="client" emptyResourceType="client" />,
});

function Component() {
  const { id } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const stack = useModalsStack(["clientForm", "confirm", "client", "force", "payment"]);

  const {
    data: client,
    isLoading,
    error,
    refetch,
  } = useQuery(
    orpc.client.get.queryOptions({
      input: { id },
    }),
  );

  if (isLoading) {
    return <ClientSkeleton />;
  }

  if (error) {
    return <StatePage error={error} reset={refetch} resourceType="client" emptyResourceType="client" />;
  }

  if (!client) {
    return null;
  }

  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        {canGoBack ? (
          <Anchor component="button" onClick={() => router.history.back()} className="flex items-center gap-1.5">
            <LucideArrowLeft className="size-6" />
          </Anchor>
        ) : null}

        <div className="flex items-center gap-1.5">
          <EditClient client={client} stack={stack} />
          <Button
            onClick={() => stack.open("clientForm")}
            size="sm"
            variant="filled"
            leftSection={<LucideEdit className="size-4" />}
          >
            {t("clients.edit.edit")}
          </Button>

          <ClientMenu
            client={client}
            target={
              <ActionIcon size="lg" variant="default" className="size-9">
                <LucideMoreVertical className="size-4" />
              </ActionIcon>
            }
          />
        </div>
      </div>

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar with main client information */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <ClientMainCard client={client} />
          <NationalIdCard
            data={
              client.encryptionKey && client.nIdFrontKey && client.nIdFrontIv && client.nIdBackKey && client.nIdBackIv
                ? {
                    encryptionKey: client.encryptionKey,
                    nIdFrontKey: client.nIdFrontKey,
                    nIdFrontIv: client.nIdFrontIv,
                    nIdBackKey: client.nIdBackKey,
                    nIdBackIv: client.nIdBackIv,
                  }
                : null
            }
          />
        </div>

        {/* Main content area - Statistics */}
        <div className="gap-md flex min-w-0 grow flex-col">
          <Card withBorder className="p-md min-w-0">
            <div className="gap-md flex flex-col">
              <div className="flex items-center gap-1.5">
                <div className="bg-indigo-light flex size-8 flex-none items-center justify-center rounded-full">
                  <LucideChartArea className="text-indigo-filled size-4" />
                </div>
                <h3 className="text-lg font-semibold">{t("clients.detail.statistics")}</h3>
              </div>

              <div
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(12.5rem, 1fr))",
                }}
                className="gap-md grid"
              >
                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("clients.totalBookings")}</div>
                  <div className="text-2xl font-bold">{client.totalBookings}</div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("clients.totalSpent")}</div>
                  <div className="text-green-filled text-2xl font-bold">{formatCurrency(client.totalSpent)}</div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("clients.totalAmountDue")}</div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(Math.abs(client.totalAmountDue))}{" "}
                    {client.totalAmountDue < 0 ? <span className="lowercase">({t("reservations.refund")})</span> : null}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("clients.lastTripDate")}</div>
                  <div className="text-2xl font-bold">
                    {client.lastTripDate ? dayjs(client.lastTripDate).format("DD MMM YYYY") : "-"}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <ClientReservations client={client} />
          <ClientTrips clientId={client.id} />
        </div>
      </div>
    </div>
  );
}

function ClientMainCard({ client }: { client: AdminClient["Client"] }) {
  const hasClientPermission = useHasPermission("client:get");
  return (
    <Card withBorder className="overflow-hidden p-0">
      {/* Avatar Section */}
      <div className="z-10 -m-px flex">
        <div className="dark:bg-primary-800 bg-primary-500 relative h-32 w-full rounded-t-md">
          <div className="absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-black/20 via-black/10 to-transparent" />
          <div className="dark:bg-dark-500 absolute bottom-0 left-1/2 z-10 flex size-20 -translate-x-1/2 translate-y-1/2 items-center justify-center overflow-hidden rounded-full bg-gray-100">
            {client.imageUrl ? (
              <img
                src={getImageUrl(client.imageUrl)}
                alt={client.name}
                className="h-full w-full rounded-full object-cover"
                draggable={false}
              />
            ) : (
              <LucideUser className="text-dimmed size-10" />
            )}
          </div>
        </div>
      </div>

      {/* Client Details */}
      <div className="gap-md p-md flex flex-col pt-12">
        <div className="text-center">
          <h1 className="text-xl font-semibold">{client.name}</h1>
        </div>

        <div className="gap-sm flex flex-col">
          <div className="gap-sm flex items-start">
            <LucideMail className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("clients.email")}</div>
              {client.email ? (
                <Anchor href={`mailto:${client.email}`} target="_blank" className="font-medium">
                  {client.email}
                </Anchor>
              ) : (
                <span className="text-dimmed">-</span>
              )}
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucidePhone className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("clients.phone")}</div>
              {client.phoneNumber ? (
                <Anchor dir="ltr" href={`tel:${client.phoneNumber}`} className="font-medium">
                  {client.phoneNumber}
                </Anchor>
              ) : (
                <span className="text-dimmed">-</span>
              )}
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideCalendar className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("clients.firstTripDate")}</div>
              <p className="font-medium">{dayjs(client.createdAt).format("DD MMM YYYY")}</p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideSwatchBook className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("clients.tags")}</div>
              <p className="flex flex-wrap items-center gap-1.5 font-medium">
                {client.tags.length ? (
                  client.tags.map((tag) => (
                    <BadgeLink
                      key={tag}
                      to="/clients"
                      size="sm"
                      className="cursor-pointer"
                      enabled={hasClientPermission}
                      search={() => ({ tags: [tag] })}
                      color={
                        CONSTANTS.MANTINE_COLORS_MAP[
                          stringToRandomNumber(tag, 0, CONSTANTS.MANTINE_COLORS_MAP.length - 1)
                        ]
                      }
                    >
                      {tag}
                    </BadgeLink>
                  ))
                ) : (
                  <span className="text-dimmed">—</span>
                )}
              </p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideNotepadText className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("clients.notes")}</div>
              <p className="font-medium">{client.notes || <span className="text-dimmed">—</span>}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ClientSkeleton() {
  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        <Skeleton className="size-6 rounded" />
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="size-9 rounded" />
        </div>
      </div>

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <Card withBorder className="overflow-hidden p-0">
            <Skeleton className="h-32 w-full rounded-b-none" />
            <div className="gap-md p-md flex flex-col pt-12">
              <div className="text-center">
                <Skeleton className="mx-auto h-6 w-32" />
              </div>
              <div className="gap-sm mt-1.5 flex flex-col">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="gap-sm flex items-start">
                    <Skeleton className="size-4 shrink-0 rounded-full" />
                    <div className="flex grow flex-col gap-1.5">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Main content area */}
        <div className="gap-md flex min-w-0 grow flex-col">
          <Card withBorder className="p-md">
            <div className="mb-sm flex items-center gap-1.5">
              <Skeleton className="size-8 rounded-full" />
              <Skeleton className="h-5 w-20" />
            </div>

            <div className="gap-md grid md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          </Card>

          <ClientReservationsSkeleton />
          <ClientTripsSkeleton />
        </div>
      </div>
    </div>
  );
}
